import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "@marekl/adminer/datetime";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, ApiError } from "../api";
import type { ID } from "../id";
import { liveCommand } from "../liveCommand";

/**
 * Soutěžní kategorie a startovní listina (Modul 7).
 *
 * Two layers, and the split matters. A *soutěžní kategorie* is what
 * actually runs: one or more rule-set Kombinace, merged when the field is
 * thin, decided per competition or for a whole series. The *startovní
 * listina* is the ordered document built from the entries.
 *
 * The list is a HIERARCHY — day → section → discipline block → entry —
 * and that shape carries the rule that a running category is never split:
 * a category is one block, so entries reorder inside it and whole blocks
 * move between sections.
 *
 * Everything derived is derived on the server — the times, the displayed
 * numbers, the category headings — so the page renders what it is given
 * and never recomputes a schedule.
 */

export interface CompetitionCategory {
  id: ID;
  /** Where the category lives: "series:12" when the whole series groups
   * together, "competition:7" otherwise. */
  scope: string;
  /** The federation's heading: "MD Dance Sólo Kadet mladší/Kadet starší B". */
  name: string;
  /** The combination whose rules the whole category runs under — its
   * time limit, deduction table and class. */
  governing_discipline_category_id: ID;
  /** Every combination the category covers; more than one means merged. */
  discipline_category_ids: ID[];
  sort_order: number;
  /** Submitted entries across every round the grouping counts. */
  entry_count: number;
  withdrawn_count: number;
  /** This competition's share of the above. Equal to entry_count unless
   * the categories are shared across a series — and where they differ,
   * the difference is the point: the merge is decided on the total, the
   * start list being built is this round's. */
  local_entry_count: number;
  local_withdrawn_count: number;
  merged: boolean;

  /** The rules the category runs under, resolved from its governing
   * combination. Stated in the list so a merged category never hides
   * whose window applies. */
  time_min_seconds?: number;
  time_max_seconds?: number;
  floor_name?: string;
  deduction_table_name?: string;
  scoring_sheet_name?: string;
  plus_judges: number;
  tech_judges: number;
  /** These categories belong to the whole series, so merging here
   * decides for every round of it. */
  shared_with_series: boolean;
  /** How many competitions the entry counts sum over — rounds of the
   * same type within the series. A final's field is drawn out of the
   * qualifiers, so adding the two would count competitors twice. */
  counted_competitions: number;
  /** The five keys of the canonical order. See compareCategoryOrder. */
  order: CategoryOrder;
}

/**
 * The canonical order of soutěžní kategorie, as the five keys a client
 * needs to reproduce it:
 *
 *   skupina disciplín → disciplína → formace (nejmenší první) →
 *   věk (nejmladší první) → třída
 *
 * Every key is a SortOrder column of the rule set, never a constant in
 * code. The federation decides that MD comes before PPD, and MD Dance
 * before MD Pom-pon, by ordering its families and disciplines that way;
 * nothing here knows those names exist. The three ladders bring their
 * own order too, which is why "smallest", "youngest" and A-before-B
 * need neither arithmetic nor an alphabet.
 *
 * The class is a position and not the code for the same reason the
 * heading is not sorted as text: `localeCompare` and Go's byte-order
 * comparison disagree about anything outside ASCII, so a class code
 * carrying a diacritic would have filed differently on the two sides of
 * the wire.
 *
 * Discipline sits second because it leads the heading: sorting formace
 * ahead of it would interleave two disciplines under one formace, which
 * reads as scrambled.
 */
export interface CategoryOrder {
  family_sort: number;
  discipline_sort: number;
  formation_sort: number;
  age_sort: number;
  class_sort: number;
}

/**
 * The client half of a twin: `CompareOrder` in
 * internal/service/competitioncategory/order.go compares the same five
 * keys in the same sequence. The two must agree — a list the server
 * sorts one way and the table sorts another is a list that reorders
 * itself the moment it refetches.
 *
 * Deliberately NOT a comparison of `name`: the heading is composed for
 * reading, so sorting it as text files DĚTI before SENIOR and Duo/trio
 * before Sólo.
 */
export function compareCategoryOrder(a: CategoryOrder, b: CategoryOrder): number {
  return (
    a.family_sort - b.family_sort ||
    a.discipline_sort - b.discipline_sort ||
    a.formation_sort - b.formation_sort ||
    a.age_sort - b.age_sort ||
    a.class_sort - b.class_sort
  );
}

/**
 * The same order packed into one sortable value, because `sortValue`
 * takes a single comparable.
 *
 * The numbers are zero-padded so they order as numbers and not as text,
 * where "10" would file before "2"; the offset covers a negative
 * sort_order, which nothing writes today and nothing forbids either.
 *
 * This must agree with compareCategoryOrder — categoryOrder.test.ts
 * asserts they produce the same sequence rather than trusting that they
 * do.
 */
export function categorySortKey(o: CategoryOrder): string {
  const pad = (n: number) => String(n + 1_000_000).padStart(9, "0");
  return [
    pad(o.family_sort),
    pad(o.discipline_sort),
    pad(o.formation_sort),
    pad(o.age_sort),
    pad(o.class_sort),
  ].join(" ");
}

/** Why a start list no longer matches its competition. */
export type StaleReason =
  | "days_missing"
  | "days_orphaned"
  | "days_extra"
  | "category_split"
  | "category_misplaced"
  | "withdrawn"
  | "empty_block"
  | "unplaced";

/**
 * Who can put a stale reason right — decided on the SERVER.
 *
 * `build` means pressing „Aktualizovat" fixes it; `manual` means only a
 * person can, because the fix is a decision rather than a derivation.
 * The page used to keep this table itself, and a page with its own
 * opinion about what the server will do is a page that will eventually
 * be wrong about it.
 */
export type Remedy = "build" | "manual";

export type StartListItemKind = "discipline" | "block" | "lanes";

/** How a skupina drah runs (R19): side by side, or taking turns. */
export type StartListLaneMode = "parallel" | "alternating";

export type StartListBlockKind =
  | "presence"
  | "warmup"
  | "briefing"
  | "break"
  | "lunch"
  | "ceremony"
  | "other";

export interface StartListCompetitor {
  first_name: string;
  last_name: string;
}

/** One performance on the printed page. */
export interface StartListEntry {
  id: ID;
  competition_entry_id: ID;
  position: number;
  /** The number to print: frozen once published, otherwise the position. */
  display_number: number;
  /** Set while the list is published; cleared again by „Zrušit
   * zveřejnění". A
   * number never MOVES while the list is up — a late entry draws the
   * next free one rather than pushing anybody along. */
  start_number?: number;
  /** ČP — číslo přihlášky. A different quantity from the start number. */
  entry_number: number;
  team_name: string;
  team_id: ID;
  /** The state a club outside ČNSMS represents; empty for our own clubs. */
  team_country?: string;
  /** The "Jméno" column: the competitor for a solo, the team otherwise. */
  title: string;
  competitors?: StartListCompetitor[];
  /** The club owes a recording and has not delivered one (Modul 6).
   * Filled server-side; a federation discipline is never missing. */
  music_missing?: boolean;
  scratched: boolean;
  scratched_at?: Dayjs;
  scratch_note?: string;
  start_at: Dayjs;
  /** Probíhá: when it is expected on a delay (R23); absent on time. */
  expected_start_at?: Dayjs;
  end_at: Dayjs;
  /** The rule set never gave this row a time limit. */
  missing_time_limit?: boolean;
  /** Inside a skupina drah (both 1-based, absent outside one): rows
   * sharing a start_index form one start. Sorting a group's rows by
   * (start_index, lane) is the order of performance. */
  lane?: number;
  start_index?: number;
}

/** A discipline block with its entries, or a manual block. */
export interface StartListItem {
  id: ID;
  kind: StartListItemKind;
  position: number;

  // kind === "discipline"
  competition_category_id?: ID;
  competition_category_name?: string;
  /** The block points at a category that no longer exists — what a prune
   * leaves behind. Explicit, because an absent name is indistinguishable
   * from a block that simply has not loaded. */
  competition_category_missing?: boolean;
  entries?: StartListEntry[];

  // kind === "block"
  block_kind?: StartListBlockKind;
  label?: string;
  duration_seconds?: number;
  /** Parallel blocks happen DURING the running order and move nothing;
   * sequential ones happen BETWEEN and push everything after them. */
  parallel: boolean;

  // kind === "lanes" (skupina drah)
  lane_mode?: StartListLaneMode;
  lane_count?: number;
  /** Each lane's discipline blocks in order; lane 1 first. */
  lanes?: StartListLane[];

  start_at: Dayjs;
  /** Probíhá: when it is expected on a delay (R23); absent on time. */
  expected_start_at?: Dayjs;
  end_at: Dayjs;
}

export interface StartListLane {
  lane: number;
  items: StartListItem[];
}

/** Every block of a list, the ones in a skupina drah included. Anything
 * reading a document's rows walks this, never `section.items` alone,
 * which misses every block in a lane (twin of Document.Blocks in Go). */
export function startListBlocks(doc: StartListDocument | undefined): StartListItem[] {
  return (doc?.days ?? []).flatMap((d) =>
    d.sections.flatMap((s) => s.items.flatMap((i) => [i, ...(i.lanes ?? []).flatMap((l) => l.items)])),
  );
}

export interface StartListSection {
  id: ID;
  name: string;
  position: number;
  items: StartListItem[];
  start_at: Dayjs;
  /** Probíhá: when it is expected on a delay (R23); absent on time. */
  expected_start_at?: Dayjs;
  end_at: Dayjs;
}

export interface StartListDay {
  /** Probíhá: how late the day runs, seconds (R23). */
  delay_seconds?: number;
  /** The competition no longer runs on this day, but something is still
   * scheduled on it. It keeps its schedule — which surviving day an
   * afternoon belongs on is a decision, not a derivation — and says so
   * instead. */
  outside_competition?: boolean;
  /** A start of the day has been called: its start time and parts stay. */
  started?: boolean;
  id: ID;
  /** Date-only (`YYYY-MM-DD`). Deliberately a string: the rest client
   * revives full ISO timestamps into Dayjs, which a date-only field must
   * not become. */
  date: string;
  start_seconds: number;
  position: number;
  sections: StartListSection[];
  start_at: Dayjs;
  end_at: Dayjs;
  /** The day's harmonogram, computed server-side from the WHOLE day, so a
   * narrowed export still carries the full schedule. */
  timetable: TimetableRow[];
}

/** One harmonogram line: „Obědová pauza 12:15 - 12:45". The label is
 * resolved server-side, because the XLSX and the PDF print it too. */
export interface TimetableRow {
  kind: "section" | "block";
  block_kind?: StartListBlockKind;
  label: string;
  start_at: Dayjs;
  /** Probíhá: when it is expected on a delay (R23); absent on time. */
  expected_start_at?: Dayjs;
  end_at: Dayjs;
}

export type StartListStage = "draft" | "preview" | "published";

export interface StartListDocument {
  id: ID;
  competition_id: ID;
  competition_name: string;
  name: string;
  /** Read from the competition, not stored on the list. */
  gap_seconds: number;
  /** R16: clubs see it from `preview` on; `published` alone has stored
   * numbers (and is what `published` says). */
  stage: StartListStage;
  published: boolean;
  published_at?: Dayjs;
  draft_published_at?: Dayjs;
  days: StartListDay[];
  ends_at: Dayjs;
  entry_count: number;
  category_count: number;
  missing_time_limits: number;
  /** Rows struck off. They are inside entry_count — a scratched
   * competitor keeps their slot and their number. */
  scratched_count: number;
  /** Ways the list has drifted from the competition it was built out of.
   * Machine keys; the Czech copy lives in `staleCopy.ts`, the way
   * dashboard alerts do. */
  stale_reasons?: StaleReason[];
  /** Per reported reason, whether „Aktualizovat" fixes it. See Remedy. */
  stale_remedies?: Partial<Record<StaleReason, Remedy>>;
  /** Submitted entries not yet on the list — waiting for „Aktualizovat". */
  unplaced_entries: number;
}

/** The list, plus how many entered combinations have no category yet. */
export interface CategoryList {
  rows: CompetitionCategory[];
  /** Combinations with entries that nothing on this page represents. The
   * page cannot derive it — what is missing is missing — so the server
   * counts it alongside the rows. */
  missing: number;
}

export function useCompetitionCategories(competitionId: ID | undefined) {
  return useQuery<CategoryList>({
    queryKey: ["competitions", "categories", competitionId],
    enabled: !!competitionId,
    // Never served from cache on arrival. See useStartList.
    staleTime: 0,
    queryFn: async () => {
      const res = await apiGet<{ data: CompetitionCategory[] | null; missing?: number }>(
        `/competitions/${competitionId}/categories`,
      );
      return { rows: res.data ?? [], missing: res.missing ?? 0 };
    },
  });
}

/**
 * The competition's one start list. A 404 means it has not been built
 * yet — or, for a club, that it is not published; the page treats both
 * the same way, which is the point of answering 404 rather than 403.
 */
/**
 * `rowFilter` is a query string narrowing the document's rows server-side
 * (`rowFilterQuery` in lib/startListExport.ts: team, scope, scratched), so
 * the printed page, the screen and the downloads are the same document.
 */
export function useStartList(competitionId: ID | undefined, rowFilter?: string) {
  return useQuery<StartListDocument>({
    queryKey: ["startlists", competitionId, rowFilter || null],
    enabled: !!competitionId,
    // Always refetched when the page is opened.
    //
    // React Query does refetch on mount by default — but only when the
    // data is STALE, and this app sets a 30-second staleTime globally
    // (createDefaultQueryClient). For most screens that is right: a list
    // of teams does not change while you walk to the coffee machine. A
    // start list does. It is a shared document — entries arrive, a club
    // withdraws, another organizer rearranges — and coming back to a
    // half-minute-old copy of it means dragging blocks around a running
    // order that has already moved. Zero says the honest thing: this is
    // never fresh enough to trust.
    staleTime: 0,
    // While the competition runs (a day has begun, or the day runs late)
    // the expected times move with the clock (R23): re-read every minute,
    // which is all a screen shows.
    refetchInterval: (q) =>
      q.state.data?.days.some((d) => d.started || d.delay_seconds) ? 60_000 : false,
    retry: false,
    queryFn: () =>
      apiGet<StartListDocument>(
        `/competitions/${competitionId}/startlist` +
          (rowFilter ? `?${rowFilter}` : ""),
      ),
  });
}

// Every write here can reshape both layers: merging categories regroups
// the list, and building it adds rows. Invalidating by key root keeps the
// views from disagreeing.
function useStartListMutation<TData, TVars>(mutationFn: (vars: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation<TData, ApiError, TVars>({
    mutationFn,
    // Every call site reports the failure itself — most through
    // `notifyError(err)`, the confirms through the dialog's
    // error slot. React Query fires the global handler IN ADDITION to a
    // mutation's own onError, so without this the running order answered a
    // refused drag with the same toast twice.
    meta: { suppressErrorToast: true },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["competitions"] }),
        qc.invalidateQueries({ queryKey: ["startlists"] }),
      ]),
  });
}

export function useGenerateCategories(competitionId: ID) {
  return useStartListMutation<{ created: number; removed: number }, void>(() =>
    apiPost(`/competitions/${competitionId}/categories/generate`, {}),
  );
}

/** One category going into a merge, with the window it runs under. */
export interface MergeMember {
  category_id: ID;
  discipline_category_id: ID;
  name: string;
  age_name: string;
  class_code: string;
  time_min_seconds: number | null;
  time_max_seconds: number | null;
  /** Marks the member whose rules would apply. */
  governing: boolean;
}

/**
 * What a merge would produce, resolved before it happens.
 *
 * Merging changes what competitors may perform, so the dialog states the
 * rules every time. Only the time window can actually differ — the rest
 * is identical by validation — which is why that is the one thing the
 * organizer chooses.
 */
export interface MergePreview {
  name: string;
  members: MergeMember[];
  floor_name?: string;
  deduction_table_name?: string;
  scoring_sheet_name?: string;
  plus_judges: number;
  tech_judges: number;
  /** The members do not share a window, so the choice is worth asking. */
  time_differs: boolean;
}

/**
 * Resolves a merge without writing anything. Refuses an impossible merge
 * here rather than after the organizer has confirmed it.
 */
export function useMergePreview(competitionId: ID) {
  return useMutation<MergePreview, unknown, { categoryIds: ID[]; governing?: ID }>({
    mutationFn: ({ categoryIds, governing }) =>
      apiPost(`/competitions/${competitionId}/categories/merge/preview`, {
        category_ids: categoryIds,
        governing_discipline_category_id: governing ?? 0,
      }),
  });
}

export function useMergeCategories(competitionId: ID) {
  return useStartListMutation<
    CompetitionCategory,
    { categoryIds: ID[]; governing?: ID }
  >(({ categoryIds, governing }) =>
    apiPost(`/competitions/${competitionId}/categories/merge`, {
      category_ids: categoryIds,
      governing_discipline_category_id: governing ?? 0,
    }),
  );
}

export function useSplitCategory(competitionId: ID) {
  return useStartListMutation<{ data: CompetitionCategory[] }, ID>((categoryId) =>
    apiPost(`/competitions/${competitionId}/categories/${categoryId}/split`, {}),
  );
}

export interface BuildResult {
  added: number;
  blocks: number;
  /** Rows taken off because their přihláška was withdrawn. An odhláška
   * is not a scratch: the entrant is not coming, so they come off the
   * running order rather than being drawn through it. */
  withdrawn: number;
  removed: number;
  /** Discipline blocks swept because nobody is left competing in them. */
  blocks_removed: number;
  /** Competition days the list had no row for. */
  days: number;
  days_removed: number;
  /** Rows moved into a different block because their category changed —
   * the merge healing itself. */
  regrouped: number;
}

/**
 * "Aktualizovat" — the only generator. Creates the list if it does
 * not exist, adds what arrived late, strikes off what was withdrawn, and
 * moves nothing.
 */
export function useBuildStartList(competitionId: ID) {
  return useStartListMutation<BuildResult, void>(() =>
    apiPost(`/competitions/${competitionId}/startlist/build`, {}),
  );
}

export interface PublishResult {
  numbers: number;
  /** The uzávěrka was moved to now, so entries are closed. */
  entries_closed: boolean;
}

export function usePublishStartList(competitionId: ID) {
  return useStartListMutation<PublishResult, { close_entries: boolean } | void>((body) =>
    apiPost(`/competitions/${competitionId}/startlist/publish`, body ?? {}),
  );
}

/** Shows the list to the clubs as a draft; numbers stay derived. */
export function usePublishStartListDraft(competitionId: ID) {
  return useStartListMutation<void, void>(() =>
    apiPost(`/competitions/${competitionId}/startlist/publish-draft`, {}),
  );
}

/**
 * Takes the list back off the wall and THAWS the numbers.
 *
 * Withdrawing is the moment an organizer rearranges, and a list whose
 * numbers no longer match its own order is not a start list — so every
 * start_number is cleared and the counter reset. The modal on the page
 * says as much („Startovní čísla se uvolní"); this comment used to say
 * the opposite.
 */
export function useUnpublishStartList(competitionId: ID) {
  return useStartListMutation<void, void>(() =>
    apiPost(`/competitions/${competitionId}/startlist/unpublish`, {}),
  );
}

export function useUpdateDay(live?: ID) {
  return useStartListMutation<unknown, { dayId: ID; start_seconds: number }>(({ dayId, ...p }) =>
    live
      ? liveCommand(live, "startlist.day_update", { day: dayId, ...p })
      : apiPatch(`/startlist/days/${dayId}`, p),
  );
}

export function useAddSection(live?: ID) {
  return useStartListMutation<unknown, { dayId: ID; name: string }>(({ dayId, name }) =>
    live
      ? liveCommand(live, "startlist.section_add", { day: dayId, name })
      : apiPost(`/startlist/days/${dayId}/sections`, { name }),
  );
}

export function useRenameSection(live?: ID) {
  return useStartListMutation<unknown, { sectionId: ID; name: string }>(({ sectionId, name }) =>
    live
      ? liveCommand(live, "startlist.section_rename", { section: sectionId, name })
      : apiPatch(`/startlist/sections/${sectionId}`, { name }),
  );
}

export function useDeleteSection(live?: ID) {
  return useStartListMutation<unknown, ID>((sectionId) =>
    live
      ? liveCommand(live, "startlist.section_delete", { section: sectionId })
      : apiDelete(`/startlist/sections/${sectionId}`),
  );
}

/**
 * Each reorder is a PUT of the whole new order for ONE parent. The body
 * IS the order, not a description of a move — idempotent, no position
 * arithmetic, and the same call serves dragging and the keyboard.
 *
 * Entries are scoped to their discipline block, and the server refuses a
 * set that is not exactly that block's rows: a category is one block, so
 * confining the call is what makes a split impossible.
 */
export function useReorderEntries(live?: ID) {
  return useStartListMutation<unknown, { itemId: ID; ids: ID[] }>(({ itemId, ids }) =>
    live
      ? liveCommand(live, "startlist.reorder_entries", { item: itemId, ids })
      : apiPut(`/startlist/items/${itemId}/entry-order`, { ids }),
  );
}

/** Reorders a section's items, adopting blocks dragged in from another
 * section — that is how a whole discipline moves to the afternoon. */
export function useReorderItems(live?: ID) {
  return useStartListMutation<unknown, { sectionId: ID; ids: ID[] }>(({ sectionId, ids }) =>
    live
      ? liveCommand(live, "startlist.reorder_items", { section: sectionId, ids })
      : apiPut(`/startlist/sections/${sectionId}/item-order`, { ids }),
  );
}

export interface BlockPayload {
  /** The item the block goes directly after. Absent means the slot
   * BEFORE the first one — the separator that opens a part has no row
   * above it to name. It does not mean "append": read that way, the
   * topmost slot put a prezence after the last performance of the day. */
  after_item_id?: ID;
  block_kind: StartListBlockKind;
  label?: string;
  duration_seconds?: number;
  parallel?: boolean;
  /** Seconds from midnight; required for a parallel block. */
  start_seconds?: number | null;
  end_seconds?: number | null;
}

export function useInsertBlock(live?: ID) {
  return useStartListMutation<unknown, { sectionId: ID } & BlockPayload>(({ sectionId, ...p }) =>
    live
      ? liveCommand(live, "startlist.block_insert", { section: sectionId, ...p })
      : apiPost(`/startlist/sections/${sectionId}/blocks`, p),
  );
}

export function useUpdateBlock(live?: ID) {
  return useStartListMutation<unknown, { itemId: ID } & BlockPayload>(({ itemId, ...p }) =>
    live
      ? liveCommand(live, "startlist.block_update", { item: itemId, ...p })
      : apiPatch(`/startlist/items/${itemId}`, p),
  );
}

export function useDeleteBlock(live?: ID) {
  return useStartListMutation<unknown, ID>((itemId) =>
    live
      ? liveCommand(live, "startlist.block_delete", { item: itemId })
      : apiDelete(`/startlist/items/${itemId}`),
  );
}

export interface LaneGroupPayload {
  lane_mode: StartListLaneMode;
  lane_count: number;
  after_item_id?: ID;
}

export function useCreateLaneGroup(live?: ID) {
  return useStartListMutation<unknown, { sectionId: ID } & LaneGroupPayload>(
    ({ sectionId, ...p }) =>
      live
        ? liveCommand(live, "startlist.lanes_create", { section: sectionId, ...p })
        : apiPost(`/startlist/sections/${sectionId}/lane-groups`, p),
  );
}

export function useUpdateLaneGroup(live?: ID) {
  return useStartListMutation<unknown, { itemId: ID } & LaneGroupPayload>(({ itemId, ...p }) =>
    live
      ? liveCommand(live, "startlist.lanes_update", { group: itemId, ...p })
      : apiPatch(`/startlist/lane-groups/${itemId}`, p),
  );
}

/** A lane's whole new order, adopting blocks dragged in from the part or
 * another lane. A block leaves a lane through useReorderItems. */
export function useReorderLane(live?: ID) {
  return useStartListMutation<unknown, { groupId: ID; lane: number; ids: ID[] }>(
    ({ groupId, lane, ids }) =>
      live
        ? liveCommand(live, "startlist.reorder_lane", { group: groupId, lane, ids })
        : apiPut(`/startlist/lane-groups/${groupId}/lanes/${lane}/item-order`, { ids }),
  );
}

/**
 * Edits a running competition's start list as live commands (R25): pass the
 * competition's id to the hooks above while it is Probíhá, nothing before.
 */
export function useScratchEntry(live?: ID) {
  return useStartListMutation<unknown, { entryRowId: ID; scratched: boolean; note?: string }>(
    ({ entryRowId, scratched, note }) =>
      live
        ? liveCommand(live, scratched ? "startlist.scratch" : "startlist.unscratch", {
            row: entryRowId,
            note: note ?? "",
          })
        : apiPost(`/startlist/entries/${entryRowId}/${scratched ? "scratch" : "unscratch"}`, {
            note: note ?? "",
          }),
  );
}
